// The base no-unused-vars rule does not see type-only uses.
/* eslint no-unused-vars: "off", "@typescript-eslint/no-unused-vars": "warn" */
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Container,
  Flashbar,
  Header,
  SpaceBetween,
  Spinner,
} from '@amzn/awsui-components-react-v3/polaris';
import ApiClient from '../../../ApiClient';
import DetailsPanel, { DetailsItem } from '../../Common/DetailsPanel';
import { Messages, RetryAlert } from '../../Common/ErrorMessages';
import { Locale, useLocales } from '../../../utils/locales';
import { convertDateTimePretty, valueOrDash } from '../../../utils/ReadableConverter';
import { ApiError } from '../../../utils/serverErrors';
import PricingModelChangeSets from '../PricingModels/PricingModelChangeSets';
import { arcLabel, formatLabel, latestChangeText, summaryOf } from '../PricingModels/pricingModels';
import { TER_GROUPS_PATH, terGroupRoute, TerGroupSummary } from '../TerGroups/terGroups';
import ChangeSetImpactPanel from '../ChangeSetImpact/ChangeSetImpactPanel';
import ChangeSetReview, { ReviewOutcomeFlash } from '../ChangeSetReview/ChangeSetReview';
import { ReviewOutcome } from '../ChangeSetReview/changeSetReview';
import { findLatestPendingChangeSetId } from '../ChangeSetImpact/changeSetImpact';
import ScopingModelFields from './ScopingModelFields';
import ScopingModelWeights from './ScopingModelWeights';
import * as scopingModels from './scopingModels';
import { Loaded, useBaseRate, useLoaded } from './useLoaded';

interface ScopingModelDetailProps {
  apiClient: {
    httpGet: (path: string) => Promise<unknown>;
    httpPost: (path: string, body: object) => Promise<unknown>;
    httpPut: (path: string, body: object) => Promise<unknown>;
  };
  weightSchemeId: string;
  // The change set that created the model, when arriving from its create.
  submittedChangeSetId?: string;
  canWrite: boolean;
}

const ErrorAlert = ({ id, messages }: { id: string; messages: string[] }): JSX.Element => (
  <Alert id={id} type="error">
    <Messages messages={messages} />
  </Alert>
);

const usesTerGroup = (draft: scopingModels.Draft | null): boolean =>
  Boolean(draft && scopingModels.isMultiplierDraft(draft) && draft.terGroupId);

/** The draft's TER group by name, or by id when the TER groups could not be loaded. */
const terGroupValue = (
  draft: scopingModels.Draft,
  terGroups: Loaded<TerGroupSummary[]>
): JSX.Element => {
  if (!terGroups.value && !terGroups.missing && terGroups.errors.length === 0) {
    return <Spinner id="terGroupLoading" />;
  }
  const terGroupId = draft.terGroupId as string;
  const summary = (terGroups.value || []).find(candidate => candidate.terGroup === terGroupId);
  return (
    <Link id="terGroupValue" to={terGroupRoute(terGroupId)}>
      {`${summary ? summary.groupName : terGroupId}, version ${valueOrDash(draft.terGroupVersion)}`}
    </Link>
  );
};

/** The latest draft's inputs, as the details panel lists them. */
const draftItems = (
  draft: scopingModels.Draft,
  locales: Locale[] | null,
  terGroups: Loaded<TerGroupSummary[]>
): DetailsItem[] => {
  const items: DetailsItem[] = scopingModels.MATCH_BANDS.map(band => ({
    label: band.label,
    value: <span id={`${band.weight}Value`}>{scopingModels.detailEntryText(draft, band)}</span>,
  }));
  if (usesTerGroup(draft)) {
    items.push({ label: 'TER group', value: terGroupValue(draft, terGroups) });
  }
  return [
    ...items,
    { label: 'Base rate version', value: valueOrDash(draft.baseRateVersion) },
    {
      label: 'Excluded arcs',
      value: (
        <span id="excludedArcsValue">
          {valueOrDash((draft.excludedArcs || []).map(arc => arcLabel(arc, locales)).join(', '))}
        </span>
      ),
    },
    {
      label: scopingModels.REPETITION.label,
      value: (
        <span id="repetitionWeightValue">
          {scopingModels.storedWeightText(draft.repetitionWeight)}
        </span>
      ),
    },
  ];
};

export const ScopingModelDetail = ({
  apiClient,
  weightSchemeId,
  submittedChangeSetId,
  canWrite,
}: ScopingModelDetailProps): JSX.Element => {
  const [model, setModel] = useState<scopingModels.ScopingModel | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadErrors, setLoadErrors] = useState<string[]>([]);
  const [editing, setEditing] = useState(false);
  const [values, setValues] = useState<scopingModels.FormValues>(scopingModels.emptyForm);
  const [fieldErrors, setFieldErrors] = useState<scopingModels.FieldErrors>({});
  const [formErrors, setFormErrors] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState<string | undefined>(submittedChangeSetId);
  const [reviewOutcome, setReviewOutcome] = useState<ReviewOutcome | null>(null);
  const { locales, errors: localeErrors, reload: reloadLocales } = useLocales(
    apiClient,
    scopingModels.describeError
  );
  const baseRate = useBaseRate(apiClient, editing);
  const latestDraft = model && model.latestChangeSet ? model.latestChangeSet.draft : null;
  const terGroups = useLoaded<TerGroupSummary[]>(
    apiClient,
    usesTerGroup(latestDraft) ? TER_GROUPS_PATH : null
  );

  const load = useCallback((): void => {
    setLoading(true);
    setLoadErrors([]);
    apiClient
      .httpGet(scopingModels.scopingModelPath(weightSchemeId))
      .then(response => setModel(response as scopingModels.ScopingModel))
      .catch((error: ApiError) => setLoadErrors(scopingModels.describeError(error)))
      .finally(() => setLoading(false));
  }, [apiClient, weightSchemeId]);

  useEffect(load, [load]);

  const reviewed = (outcome: ReviewOutcome): void => {
    setReviewOutcome(outcome);
    setSubmitted(undefined);
    load();
  };

  if (loading) {
    return (
      <Box id="scopingModelLoading" textAlign="center">
        <Spinner /> Loading scoping model
      </Box>
    );
  }
  if (!model) {
    return (
      <RetryAlert
        id="scopingModelLoadError"
        header="Could not load the scoping model"
        messages={loadErrors}
        retryId="retryScopingModel"
        onRetry={load}
      />
    );
  }

  const draft = model.latestChangeSet && model.latestChangeSet.draft;
  const pendingChangeSetId = findLatestPendingChangeSetId(model.changeSets);

  const startEditing = (): void => {
    setValues(scopingModels.formFromModel(model));
    setFieldErrors({});
    setFormErrors([]);
    setSubmitted(undefined);
    setEditing(true);
  };

  const change = (field: scopingModels.FormField, value: string | string[]): void => {
    setValues({ ...values, [field]: value });
    setFieldErrors({ ...fieldErrors, [field]: undefined });
  };

  const saved = (changeSet: scopingModels.ChangeSet): void => {
    setModel({
      ...model,
      latestChangeSetId: changeSet.changeSetId,
      latestChangeSetAt: changeSet.createdAt,
      latestChangeSetAuthor: changeSet.author,
      pendingChangeSetCount: model.pendingChangeSetCount + 1,
      latestChangeSet: changeSet,
      changeSets: [summaryOf(changeSet), ...model.changeSets],
    });
    setSubmitted(changeSet.changeSetId);
    setEditing(false);
  };

  const save = (): void => {
    setFormErrors([]);
    const validationErrors = scopingModels.validateForm(values, false);
    setFieldErrors(validationErrors);
    if (scopingModels.hasFieldErrors(validationErrors)) {
      return;
    }
    const request = scopingModels.toUpdateRequest(values);
    setSubmitting(true);
    apiClient
      .httpPut(scopingModels.scopingModelPath(weightSchemeId), request)
      .then(response => {
        setSubmitting(false);
        saved(response as scopingModels.ChangeSet);
      })
      .catch((error: ApiError) => {
        setSubmitting(false);
        const failure = scopingModels.describeSaveError(error, request, values, locales);
        setFieldErrors(failure.fieldErrors);
        setFormErrors(failure.formErrors);
      });
  };

  return (
    <SpaceBetween size="m">
      <ReviewOutcomeFlash outcome={reviewOutcome} onDismiss={(): void => setReviewOutcome(null)} />
      {submitted && (
        <Flashbar
          id="scopingModelSubmitted"
          items={[
            { type: 'success', content: `Submitted change set ${submitted} (pending approval)` },
          ]}
        />
      )}
      {formErrors.length > 0 && <ErrorAlert id="scopingModelFormErrors" messages={formErrors} />}
      {loadErrors.length > 0 && (
        <RetryAlert
          id="scopingModelLoadError"
          header="Could not reload the scoping model"
          messages={loadErrors}
          retryId="retryScopingModel"
          onRetry={load}
        />
      )}
      <Header
        variant="h2"
        actions={
          canWrite && !editing ? (
            <Button id="editScopingModel" onClick={startEditing}>
              Edit
            </Button>
          ) : (
            undefined
          )
        }
      >
        {model.name}
      </Header>
      {!editing && localeErrors.length > 0 && (
        <RetryAlert
          id="scopingModelLocalesError"
          header="Could not load the locales, so arcs show their locale codes"
          messages={localeErrors}
          retryId="retryLocales"
          onRetry={reloadLocales}
        />
      )}
      <DetailsPanel
        items={[
          { label: 'Name', value: <span id="nameValue">{model.name}</span> },
          {
            label: 'Description',
            value: <span id="descriptionValue">{valueOrDash(model.description)}</span>,
          },
          ...(draft ? draftItems(draft, locales, terGroups) : []),
          {
            label: 'Created',
            value: `${convertDateTimePretty(model.createdAt)} by ${model.author}`,
          },
          {
            label: 'Latest change',
            value: `${latestChangeText(model.latestChangeSetAt, model.latestChangeSetAuthor)}${
              model.latestChangeSet ? `, ${formatLabel(model.latestChangeSet.status)}` : ''
            }`,
          },
          { label: 'Pending change sets', value: model.pendingChangeSetCount },
        ]}
      />
      {editing ? (
        <Container
          header={
            <Header
              variant="h2"
              description={model.liveChangeSet ? 'Starts from the live version.' : undefined}
            >
              Edit scoping model
            </Header>
          }
        >
          <SpaceBetween size="m">
            <ScopingModelFields
              apiClient={apiClient}
              baseRate={baseRate}
              values={values}
              fieldErrors={fieldErrors}
              disabled={submitting}
              creating={false}
              onChange={change}
            />
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                id="saveScopingModel"
                variant="primary"
                loading={submitting}
                disabled={submitting || baseRate.missing || !scopingModels.readyToSubmit(values)}
                onClick={save}
              >
                Save
              </Button>
              <Button
                id="cancelScopingModel"
                disabled={submitting}
                onClick={(): void => {
                  setEditing(false);
                  setFormErrors([]);
                }}
              >
                Cancel
              </Button>
            </SpaceBetween>
          </SpaceBetween>
        </Container>
      ) : (
        draft && <ScopingModelWeights modifiers={draft.basePriceModifiers || []} />
      )}
      <PricingModelChangeSets id="scopingModelChangeSets" changeSets={model.changeSets} />
      {pendingChangeSetId && (
        <ChangeSetImpactPanel
          apiClient={apiClient}
          changeSetId={pendingChangeSetId}
          locales={locales}
          actions={
            canWrite && !editing ? (
              <ChangeSetReview
                key={pendingChangeSetId}
                apiClient={apiClient}
                changeSetId={pendingChangeSetId}
                onReviewed={reviewed}
              />
            ) : (
              undefined
            )
          }
        />
      )}
    </SpaceBetween>
  );
};

ScopingModelDetail.propTypes = {
  apiClient: PropTypes.object.isRequired,
  weightSchemeId: PropTypes.string.isRequired,
  submittedChangeSetId: PropTypes.string,
  canWrite: PropTypes.bool.isRequired,
};

const ScopingModelDetailView = (props: Omit<ScopingModelDetailProps, 'apiClient'>): JSX.Element => (
  <ScopingModelDetail apiClient={ApiClient} {...props} />
);

export default ScopingModelDetailView;
